// src/pages/Competition/EditCompetitionModal.jsx
import React, { useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "./../../components/Button/Button";
import {
  updateCompetition,
  getCompetitionById,
} from "./../../services/competitionsServices";
import { toast } from "react-toastify";
import { useUser } from "./../../contexts/UserContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

/* SlideOver aligné sur le style de création */
const SlideOver = ({ open, onClose, title, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative ml-auto h-full w-full max-w-xl bg-white dark:bg-slate-900 shadow-xl
                   border-l border-gray-200 dark:border-slate-700 p-5 sm:p-6
                   transform transition translate-x-0 animate-[mmSlideIn_220ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 rounded-full bg-black/10 dark:bg-white/10 hover:opacity-90 flex items-center justify-center text-gray-700 dark:text-gray-200"
          >
            ×
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
};

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};
const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "align",
  "link",
];

const EditCompetitionModal = ({
  isOpen,
  onClose,
  refreshList,
  competitionId,
}) => {
  const { user } = useUser();
  const userId = user?.userId;
  const isDemoUser = user?.role_id === 2;
  const isClientUser = user?.role_id === 3;
  const disableCheckboxes = isDemoUser || isClientUser;

  const [competitionData, setCompetitionData] = useState({
    nom_competition: "",
    level_competition: "",
    lieu_competition: "",
    date_competition: "",
    organisateur_competition: userId,
    horaires_pesees: "",
    photo_URL: "",
    disciplines_competition: ["Judo"],
    is_active: true,
    is_public: true,
  });

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !competitionId) return;
      try {
        const data = await getCompetitionById(competitionId);
        let parsedDisciplines;
        try {
          parsedDisciplines = Array.isArray(data.disciplines_competition)
            ? data.disciplines_competition
            : JSON.parse(data.disciplines_competition || '["Judo"]');
        } catch {
          parsedDisciplines = ["Judo"];
        }
        setCompetitionData({
          nom_competition: data.nom_competition || "",
          level_competition: data.level_competition || "",
          lieu_competition: data.lieu_competition || "",
          date_competition: data.date_competition
            ? new Date(data.date_competition).toISOString().split("T")[0]
            : "",
          organisateur_competition: data.organisateur_competition || userId,
          horaires_pesees: data.horaires_pesees || "",
          photo_URL: data.photo_URL || "",
          disciplines_competition: parsedDisciplines,
          is_active: !!data.is_active,
          is_public: !!data.is_public,
        });
      } catch (err) {
        console.error(err);
        toast.error("Erreur lors du chargement des données de la compétition");
      }
    };
    load();
  }, [isOpen, competitionId, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompetitionData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDiscipline = (discipline, checked) => {
    setCompetitionData((prev) => {
      const cur = Array.isArray(prev.disciplines_competition)
        ? prev.disciplines_competition
        : [];
      const next = checked
        ? [...cur, discipline]
        : cur.filter((d) => d !== discipline);
      return { ...prev, disciplines_competition: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateCompetition(competitionId, { ...competitionData });
      toast.success("Compétition mise à jour avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour de la compétition");
    }
  };

  return (
    <SlideOver open={isOpen} onClose={onClose} title="Éditer la compétition">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">Nom de la compétition</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              type="text"
              name="nom_competition"
              value={competitionData.nom_competition}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Niveau</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              type="text"
              name="level_competition"
              value={competitionData.level_competition}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Lieu</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              type="text"
              name="lieu_competition"
              value={competitionData.lieu_competition}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Date</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              type="date"
              name="date_competition"
              value={competitionData.date_competition}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* React Quill pour les horaires de pesées */}
        <div>
          <label className="block text-sm mb-1">
            Horaires de pesées (texte enrichi)
          </label>
          <div className="rounded-md border border-gray-300 dark:border-slate-600 overflow-hidden">
            <ReactQuill
              theme="snow"
              value={competitionData.horaires_pesees}
              onChange={(html) =>
                setCompetitionData((s) => ({ ...s, horaires_pesees: html }))
              }
              modules={quillModules}
              formats={quillFormats}
              className="min-h-[160px] bg-white dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1">Photo (URL)</label>
          <input
            className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            type="url"
            name="photo_URL"
            value={competitionData.photo_URL}
            onChange={handleChange}
            placeholder="https://…"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">
            Disciplines autorisées
          </label>
          <div className="flex flex-wrap gap-3">
            {[
              "Judo",
              "Ne-Waza",
              "JJB",
              "Jujitsu Combat",
              "Jujitsu Duo",
              "Arbitre",
              "Commissaire Sportif",
              "Judo Adapté"
            ].map((d) => (
              <label key={d} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={
                    !!competitionData.disciplines_competition?.includes(d)
                  }
                  onChange={(e) => toggleDiscipline(d, e.target.checked)}
                />
                {d}
              </label>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label
            className={`inline-flex items-center gap-2 text-sm ${disableCheckboxes ? "opacity-60" : ""}`}
          >
            <input
              type="checkbox"
              name="is_active"
              checked={competitionData.is_active}
              onChange={(e) =>
                setCompetitionData((prev) => ({
                  ...prev,
                  is_active: e.target.checked,
                }))
              }
              disabled={disableCheckboxes}
            />
            Activer la compétition
          </label>
          <label
            className={`inline-flex items-center gap-2 text-sm ${disableCheckboxes ? "opacity-60" : ""}`}
          >
            <input
              type="checkbox"
              name="is_public"
              checked={competitionData.is_public}
              onChange={(e) =>
                setCompetitionData((prev) => ({
                  ...prev,
                  is_public: e.target.checked,
                }))
              }
              disabled={disableCheckboxes}
            />
            Rendre la compétition publique
          </label>
        </div>
        {disableCheckboxes && (
          <div className="text-sm text-yellow-700 bg-yellow-100 border border-yellow-400 rounded-md p-2">
            Ces options ne sont pas disponibles avec votre formule actuelle.
          </div>
        )}

        <div className="pt-2 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            icon={FaTimes}
            onClick={onClose}
            className="rounded-lg"
          >
            Fermer
          </Button>
          <Button type="submit" icon={FaSave} className="rounded-lg">
            Mettre à jour
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

export default EditCompetitionModal;
